import { useCallback, useEffect, useRef, useState } from 'react';
import useFollowerStore from '@/stores/FollowerStore';
import useUserStore from '@/stores/UserStore';
import { Loader2 } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import {
  fetchHistoryFollowersForSelfServiceOpens,
  fetchSearchFollowersForSelfServiceOpens,
  processSelfServiceFollowerAction,
  regenerateOpensMessage,
} from '@/hooks/follower';

import { ScrollArea } from '../ui/scroll-area';
import FollowerEmptyState from './FollowerEmptyState';
import FollowerErrorState from './FollowerErrorState';
import FollowerLoadingState from './FollowerLoadingState';
import SelfServiceBulkActions from './SelfServiceBulkActions';
import SelfServiceCard from './SelfServiceCard';
import SelfServiceFilters from './SelfServiceFilters';
import SelfServiceHistoryFilters from './SelfServiceHistoryFilters';

// Constants for self-service actions
const STATUS_MAP = {
  mark_sent: 'sent',
  skip: 'skipped',
  not_found: 'not_found',
};

const ACTION_LABELS = {
  mark_sent: 'Mark as Sent',
  skip: 'Skip',
  not_found: 'Not Found',
};

const SelfServiceOpens = () => {
  const { ref: inViewRef, inView } = useInView();
  const navigate = useNavigate();

  const selfServiceOpensEnabled = useUserStore(
    (state) => state.selfServiceOpensEnabled
  );

  const {
    activeFilter,
    searchQuery,
    advancedFilters,
    followers,
    followersLoading,
    followersError,
    page,
    followerCount,
    totals,
    historyDate,
    historyActionType,
    historySummary,
    messages_pending,
    setActiveFilter,
    setSearchQuery,
    setFollowers,
    setNextFollowers,
    setFollowersLoading,
    setFollowersError,
    setTotals,
    setHistoryDate,
    setHistoryActionType,
    setHistorySummary,
    setMessagesPending,
    updatePersonalizedOpening,
    updateTotalsOnSelfServiceAction,
    updateFollowersFromResponse,
    removeFollowersFromStore,
    getFilteredFollowers,
    setSearchFollower,
    updateMessageOlderThanPrompt,
  } = useFollowerStore(
    useShallow((state) => ({
      activeFilter: state.activeFilter,
      searchQuery: state.searchQuery,
      advancedFilters: state.advancedFilters,
      followers: state.followers,
      followersLoading: state.followersLoading,
      followersError: state.followersError,
      page: state.page,
      followerCount: state.followerCount,
      totals: state.totals,
      historyDate: state.historyDate,
      historyActionType: state.historyActionType,
      historySummary: state.historySummary,
      messages_pending: state.messages_pending,
      setActiveFilter: state.setActiveFilter,
      setSearchQuery: state.setSearchQuery,
      setFollowers: state.setFollowers,
      setNextFollowers: state.setNextFollowers,
      setFollowersLoading: state.setFollowersLoading,
      setFollowersError: state.setFollowersError,
      setTotals: state.setTotals,
      setHistoryDate: state.setHistoryDate,
      setHistoryActionType: state.setHistoryActionType,
      setHistorySummary: state.setHistorySummary,
      setMessagesPending: state.setMessagesPending,
      updatePersonalizedOpening: state.updatePersonalizedOpening,
      updateTotalsOnSelfServiceAction: state.updateTotalsOnSelfServiceAction,
      updateFollowersFromResponse: state.updateFollowersFromResponse,
      removeFollowersFromStore: state.removeFollowersFromStore,
      getFilteredFollowers: state.getFilteredFollowers,
      setSearchFollower: state.setSearchFollower,
      updateMessageOlderThanPrompt: state.updateMessageOlderThanPrompt,
    }))
  );

  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [selectedFollowers, setSelectedFollowers] = useState(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [removingFollowers, setRemovingFollowers] = useState(new Set());
  const [loadingFollowers, setLoadingFollowers] = useState(new Map());
  const [filterLoading, setFilterLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(null);
  const scrollAreaRef = useRef(null);

  // Fetch followers with proper loading and error handling
  const fetchData = useCallback(
    async (type, page, activeFilter, searchQuery, advancedFilters) => {
      try {
        setFollowersLoading(true);
        setFollowersError(null);

        if (type === 'new') {
          setFilterLoading(true);
        }

        let response;

        // Handle history filter differently
        if (activeFilter === 'history') {
          response = await fetchHistoryFollowersForSelfServiceOpens(
            page,
            historyDate,
            historyActionType
          );
        } else {
          // Build combined filters
          let combinedFilters = Array.isArray(advancedFilters)
            ? advancedFilters.map((group) =>
                Array.isArray(group) ? [...group] : []
              )
            : [];

          const andIntoFirstGroup = (condition) => {
            if (!condition) return;
            if (
              !Array.isArray(combinedFilters) ||
              combinedFilters.length === 0
            ) {
              combinedFilters = [[condition]];
            } else {
              combinedFilters[0] = [...combinedFilters[0], condition];
            }
          };

          // personalized_action_status from activeFilter (except 'all')
          if (
            activeFilter === 'sent' ||
            activeFilter === 'skipped' ||
            activeFilter === 'not_found' ||
            activeFilter === 'pending'
          ) {
            andIntoFirstGroup({
              field: 'self_service_action',
              operator: 'contains_exactly',
              value: activeFilter,
            });
          }

          // username search
          const trimmedSearch =
            typeof searchQuery === 'string' ? searchQuery.trim() : '';
          if (trimmedSearch) {
            andIntoFirstGroup({
              field: 'username',
              operator: 'contains_exactly',
              value: trimmedSearch,
            });
          }

          const params =
            combinedFilters.length > 0 ? { filters: combinedFilters } : {};

          response = await fetchSearchFollowersForSelfServiceOpens(
            page,
            params
          );
        }

        if (type === 'new') {
          const {
            data,
            next_page_url,
            current_page,
            total,
            totals,
            history_summary,
            messages_pending,
          } = response;
          setFollowers(data, next_page_url ? current_page + 1 : null, total);
          setSearchFollower(response);

          if (totals) {
            setTotals(totals);
          }

          if (history_summary) {
            setHistorySummary(history_summary);
          }

          if (messages_pending) {
            setMessagesPending(messages_pending);
          }
        } else {
          const { data, next_page_url, current_page, total } = response;
          setNextFollowers(
            data,
            next_page_url ? current_page + 1 : null,
            total
          );
        }

        setFollowersLoading(false);
        setFilterLoading(false);
      } catch (error) {
        console.error('Error fetching followers:', error);
        setFollowersError(error.message || 'Failed to fetch followers');
        setFollowersLoading(false);
        setFilterLoading(false);
      }
    },
    [
      setFollowersLoading,
      setFollowersError,
      setFollowers,
      setNextFollowers,
      setSearchFollower,
      setTotals,
      setHistorySummary,
      setMessagesPending,
      historyDate,
      historyActionType,
    ]
  );

  useEffect(() => {
    if (selfServiceOpensEnabled === false) {
      navigate('/inbox');
    } else if (selfServiceOpensEnabled === true) {
      // Set initial historyActionType to 'sent' for self-service
      if (
        historyActionType === 'approved' ||
        historyActionType === 'rejected'
      ) {
        setHistoryActionType('sent');
      }
    }
  }, [
    selfServiceOpensEnabled,
    navigate,
    historyActionType,
    setHistoryActionType,
  ]);

  // Initial load
  useEffect(() => {
    fetchData('new', 1, activeFilter, searchQuery, advancedFilters);
  }, [fetchData]);

  // Reset and reload when filters change
  useEffect(() => {
    fetchData('new', 1, activeFilter, searchQuery, advancedFilters);
  }, [
    activeFilter,
    searchQuery,
    advancedFilters,
    historyDate,
    historyActionType,
    fetchData,
  ]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (inView) {
      fetchData('next', page, activeFilter, searchQuery, advancedFilters);
    }
  }, [inView, fetchData]);

  // Reset selected followers on filter change
  useEffect(() => {
    if (isMultiSelectMode) {
      setSelectedFollowers(new Set());
    }
  }, [isMultiSelectMode, activeFilter]);

  if (!selfServiceOpensEnabled) {
    return null;
  }

  const filteredFollowers = getFilteredFollowers();
  const selectedCount = selectedFollowers.size;

  const handleEdit = (follower, value) => {
    setEditingId(follower._id);
    setEditValue(value || follower.opens_message?.text || '');
  };

  const handleSave = (followerId) => {
    updatePersonalizedOpening(followerId, editValue);
    setEditingId(null);
    setEditValue('');
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleBulkAction = (action) => {
    const selectedFollowerIds = Array.from(selectedFollowers);

    const previousStatuses = {};
    selectedFollowerIds.forEach((followerId) => {
      const follower = followers.find((f) => f._id === followerId);
      // When on a filter, use the activeFilter as previous status, otherwise read from follower
      previousStatuses[followerId] =
        activeFilter !== 'all' && activeFilter !== 'history'
          ? activeFilter
          : follower?.personalized_action_status || 'pending';
    });

    // Map action to status for self-service API
    const status = STATUS_MAP[action] || action;

    setLoadingFollowers(new Map(selectedFollowerIds.map((id) => [id, action])));
    setBulkLoading(action);

    toast.promise(
      processSelfServiceFollowerAction(selectedFollowerIds, status),
      {
        loading: `Processing ${selectedFollowerIds.length} followers...`,
        success: (response) => {
          const { processed_ids, unprocessed_ids } = response;

          setSelectedFollowers(new Set());
          setLoadingFollowers(new Map());
          setBulkLoading(null);

          if (unprocessed_ids.length > 0) {
            removeFollowersFromStore(unprocessed_ids);
          }

          if (processed_ids.length > 0) {
            const newStatus = STATUS_MAP[action] || action;

            // Handle differently based on filter
            if (activeFilter !== 'all') {
              // For specific filters, use removal animation
              setRemovingFollowers(new Set(processed_ids));

              // Use setTimeout to allow animation to play before removing from store
              setTimeout(() => {
                removeFollowersFromStore(processed_ids);
                setRemovingFollowers(new Set());
              }, 300);
            } else {
              // For "all" filter, immediately update status without animation
              updateFollowersFromResponse(
                processed_ids,
                unprocessed_ids,
                newStatus
              );
            }

            // Update totals with previous statuses
            updateTotalsOnSelfServiceAction(
              processed_ids,
              action,
              previousStatuses
            );
          }

          if (processed_ids.length > 0) {
            if (unprocessed_ids.length > 0) {
              return `${ACTION_LABELS[action]} ${processed_ids.length} followers. ${unprocessed_ids.length} failed.`;
            }
            return `Successfully ${ACTION_LABELS[action].toLowerCase()} ${
              processed_ids.length
            } followers!`;
          } else {
            return `No followers were ${ACTION_LABELS[action].toLowerCase()}.`;
          }
        },
        error: (error) => {
          setLoadingFollowers(new Map());
          setBulkLoading(null);
          console.error(`Error processing bulk ${action}:`, error);
          const serverMessage = error.response?.data?.message;
          return (
            serverMessage ||
            `Failed to ${ACTION_LABELS[action].toLowerCase()} followers`
          );
        },
      }
    );
  };

  const handleStatusChange = (followerId, newStatus) => {
    const follower = followers.find((f) => f._id === followerId);
    // When on a filter, use the activeFilter as previous status, otherwise read from follower
    const previousStatus =
      activeFilter !== 'all' && activeFilter !== 'history'
        ? activeFilter
        : follower?.personalized_action_status || 'pending';

    setLoadingFollowers(new Map([[followerId, newStatus]]));

    // Map action to status for self-service API
    const status = STATUS_MAP[newStatus] || newStatus;

    toast.promise(processSelfServiceFollowerAction([followerId], status), {
      loading: `${ACTION_LABELS[newStatus] || newStatus} follower...`,
      success: (response) => {
        const { processed_ids, unprocessed_ids } = response;

        if (unprocessed_ids.length > 0) {
          removeFollowersFromStore(unprocessed_ids);
        }

        if (processed_ids.includes(followerId)) {
          setLoadingFollowers(new Map());

          // Handle differently based on filter
          if (activeFilter !== 'all') {
            // For specific filters, use removal animation
            setRemovingFollowers(new Set([followerId]));

            // Use setTimeout to allow animation to play before removing from store
            setTimeout(() => {
              removeFollowersFromStore(processed_ids);
              setRemovingFollowers(new Set());
            }, 300);
          } else {
            // For "all" filter, immediately update status without animation
            updateFollowersFromResponse(processed_ids, unprocessed_ids, status);
          }

          // Update totals with previous status
          updateTotalsOnSelfServiceAction(processed_ids, newStatus, {
            [followerId]: previousStatus,
          });

          return `Successfully ${
            ACTION_LABELS[newStatus]?.toLowerCase() || newStatus
          } follower!`;
        } else {
          setLoadingFollowers(new Map());
          return `Failed to ${
            ACTION_LABELS[newStatus]?.toLowerCase() || newStatus
          } follower`;
        }
      },
      error: (error) => {
        setLoadingFollowers(new Map());
        console.error(`Error ${newStatus}:`, error);
        const serverMessage = error.response?.data?.message;
        return (
          serverMessage ||
          `Failed to ${
            ACTION_LABELS[newStatus]?.toLowerCase() || newStatus
          } follower`
        );
      },
    });
  };

  const handleSelectFollower = (followerId) => {
    const newSelected = new Set(selectedFollowers);
    if (newSelected.has(followerId)) {
      newSelected.delete(followerId);
    } else {
      newSelected.add(followerId);
    }
    setSelectedFollowers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedCount === filteredFollowers.length) {
      setSelectedFollowers(new Set());
    } else {
      setSelectedFollowers(new Set(filteredFollowers.map((f) => f._id)));
    }
  };

  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(!isMultiSelectMode);
    setSelectedFollowers(new Set());
  };

  const handleRegenerate = async (followerId) => {
    try {
      const response = await regenerateOpensMessage(followerId);

      if (response?.data?.results && response.data.results.length > 0) {
        const result = response.data.results[0];

        if (result.status === 'success' && result.generated_message) {
          // Update the follower's message in the store
          updatePersonalizedOpening(
            result.follower_id,
            result.generated_message
          );
          // Update message_older_than_prompt to false
          updateMessageOlderThanPrompt(result.follower_id, false);
          toast.success('Message regenerated successfully');
        } else if (result.status === 'skipped' && result.reason) {
          // Show skip reason as error toast
          toast.error(result.reason || 'Message regeneration was skipped');
        } else {
          toast.error('Failed to regenerate message');
        }
      } else {
        toast.error('Failed to regenerate message');
      }
    } catch (error) {
      const serverMessage = error.response?.data?.message;
      toast.error(serverMessage || 'Failed to regenerate message');
    }
  };

  // Loading state
  if (followersLoading && followers.length === 0) {
    return <FollowerLoadingState />;
  }

  // Filter loading state
  if (filterLoading && followers.length > 0) {
    return (
      <div className='flex h-full flex-col'>
        <div className='p-5'>
          <div className='flex-shrink-0 space-y-4 rounded-lg bg-muted/50 p-4'>
            {activeFilter === 'history' ? (
              <SelfServiceHistoryFilters
                historyDate={historyDate}
                historyActionType={historyActionType}
                historySummary={historySummary}
                onDateChange={setHistoryDate}
                onActionTypeChange={setHistoryActionType}
                onNavigateToPending={() => setActiveFilter('pending')}
                isLoading={followersLoading || filterLoading}
              />
            ) : (
              <SelfServiceFilters
                activeFilter={activeFilter}
                searchQuery={searchQuery}
                isMultiSelectMode={isMultiSelectMode}
                filteredFollowersLength={filteredFollowers.length}
                totals={totals}
                onFilterChange={setActiveFilter}
                onSearchChange={setSearchQuery}
                onMultiSelectToggle={toggleMultiSelectMode}
                isLoading={followersLoading || filterLoading}
              />
            )}

            <SelfServiceBulkActions
              isMultiSelectMode={isMultiSelectMode}
              selectedCount={selectedCount}
              totalFollowersCount={followerCount}
              currentPageFollowersCount={filteredFollowers.length}
              bulkLoading={bulkLoading}
              activeFilter={activeFilter}
              onSelectAll={handleSelectAll}
              onBulkMarkSent={() => handleBulkAction('mark_sent')}
              onBulkSkip={() => handleBulkAction('skip')}
              onBulkNotFound={() => handleBulkAction('not_found')}
            />
          </div>
        </div>

        <div className='relative mt-4 min-h-0 flex-1'>
          <div className='absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-sm'>
            <div className='flex items-center gap-2 text-sm text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              <span>
                Loading {activeFilter === 'all' ? 'all' : activeFilter}{' '}
                followers...
              </span>
            </div>
          </div>

          <div className='pointer-events-none opacity-50'>
            <ScrollArea ref={scrollAreaRef} className='h-full'>
              <div className='pl-1 pr-4 pt-1'>
                {filteredFollowers.length === 0 ? (
                  <FollowerEmptyState />
                ) : (
                  <>
                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4'>
                      {filteredFollowers.map((follower, index) => (
                        <SelfServiceCard
                          key={follower._id}
                          follower={follower}
                          isSelected={selectedFollowers.has(follower._id)}
                          isMultiSelectMode={isMultiSelectMode}
                          isEditing={editingId === follower._id}
                          editValue={editValue}
                          isRemoving={removingFollowers.has(follower._id)}
                          loadingAction={loadingFollowers.get(follower._id)}
                          activeFilter={activeFilter}
                          onSelect={handleSelectFollower}
                          onEdit={handleEdit}
                          onSave={handleSave}
                          onCancel={handleCancel}
                          onStatusChange={handleStatusChange}
                          onRegenerate={handleRegenerate}
                          inViewRef={inViewRef}
                          isLastItem={index === filteredFollowers.length - 1}
                          hasNextPage={page !== null && page !== 0}
                        />
                      ))}
                    </div>

                    {page !== null && page !== 0 && (
                      <div className='flex items-center justify-center py-3'>
                        <Loader2 className='h-4 w-4 animate-spin' />
                      </div>
                    )}
                  </>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (followersError && followers.length === 0) {
    return (
      <FollowerErrorState
        error={followersError}
        onRetry={() => fetchData('new', 1)}
      />
    );
  }

  return (
    <div className='flex h-full flex-col p-5'>
      <div className='flex-shrink-0 space-y-4 rounded-lg bg-muted/50 p-4'>
        {activeFilter === 'history' ? (
          <SelfServiceHistoryFilters
            historyDate={historyDate}
            historyActionType={historyActionType}
            historySummary={historySummary}
            onDateChange={setHistoryDate}
            onActionTypeChange={setHistoryActionType}
            onNavigateToPending={() => setActiveFilter('pending')}
            isLoading={followersLoading || filterLoading}
          />
        ) : (
          <SelfServiceFilters
            activeFilter={activeFilter}
            searchQuery={searchQuery}
            isMultiSelectMode={isMultiSelectMode}
            filteredFollowersLength={filteredFollowers.length}
            totals={totals}
            onFilterChange={setActiveFilter}
            onSearchChange={setSearchQuery}
            onMultiSelectToggle={toggleMultiSelectMode}
            isLoading={followersLoading || filterLoading}
          />
        )}

        <SelfServiceBulkActions
          isMultiSelectMode={isMultiSelectMode}
          selectedCount={selectedCount}
          totalFollowersCount={followerCount}
          currentPageFollowersCount={filteredFollowers.length}
          bulkLoading={bulkLoading}
          activeFilter={activeFilter}
          onSelectAll={handleSelectAll}
          onBulkMarkSent={() => handleBulkAction('mark_sent')}
          onBulkSkip={() => handleBulkAction('skip')}
          onBulkNotFound={() => handleBulkAction('not_found')}
        />
      </div>

      {messages_pending > 0 && (
        <div className='mt-4 inline-flex items-center justify-center rounded-sm border border-dashed px-3 py-1.5 text-xs text-muted-foreground sm:text-sm'>
          {messages_pending} {messages_pending === 1 ? 'message' : 'messages'}{' '}
          getting generated
        </div>
      )}

      <div className='mt-4 min-h-0 flex-1'>
        <ScrollArea ref={scrollAreaRef} className='h-full'>
          <div className='pl-1 pr-4 pt-1'>
            {filteredFollowers.length === 0 ? (
              <FollowerEmptyState />
            ) : (
              <>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4'>
                  {filteredFollowers.map((follower, index) => (
                    <SelfServiceCard
                      key={follower._id}
                      follower={follower}
                      isSelected={selectedFollowers.has(follower._id)}
                      isMultiSelectMode={isMultiSelectMode}
                      isEditing={editingId === follower._id}
                      editValue={editValue}
                      isRemoving={removingFollowers.has(follower._id)}
                      loadingAction={loadingFollowers.get(follower._id)}
                      activeFilter={activeFilter}
                      onSelect={handleSelectFollower}
                      onEdit={handleEdit}
                      onSave={handleSave}
                      onCancel={handleCancel}
                      onStatusChange={handleStatusChange}
                      onRegenerate={handleRegenerate}
                      inViewRef={inViewRef}
                      isLastItem={index === filteredFollowers.length - 1}
                      hasNextPage={page !== null && page !== 0}
                    />
                  ))}
                </div>

                {page !== null && page !== 0 && (
                  <div className='flex items-center justify-center py-3'>
                    <Loader2 className='h-4 w-4 animate-spin' />
                  </div>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default SelfServiceOpens;
